import { JobType, Routing, Search, SortKeys } from "@common/_constants";
import { formatClientRoute, LocationService } from "@common/_services";
import { Button } from "@common/components/Atoms/Button";
import Card, { CardItem } from "@common/components/Atoms/Card";
import Error from "@common/components/Atoms/Error";
import { FormHandler, Select } from "@common/components/Atoms/FormInputs";
import Loading from "@common/components/Atoms/Loading";
import { SearchEntry } from "@common/components/Atoms/SearchFilters";
import Sort from "@common/components/Atoms/Sort";
import Dropdown, {
    DropdownItem,
    DropdownMenu,
} from "@common/components/Molecules/Dropdown";
import Facets from "@common/components/Molecules/Facets";
import FilteredList, {
    FilteredListCardsView,
    FilteredListTableView,
} from "@common/components/Molecules/FilteredList";
import Modal from "@common/components/Molecules/Modal";
import SelectedFacets from "@common/components/Molecules/SelectedFacets";
import {
    JobsPageHeaderCenter,
    JobsSortDropdown,
} from "@common/components/Organisms/Jobs";
import { Facet as FacetModel, FormField } from "@common/models";
import { GlobalConfig } from "@common/utils";
import {
    JobService,
    SearchOptions,
    SearchService,
    SettingsService,
} from "@pro/_services";
import { ApplyForJob, ApplyThankYou } from "@pro/containers/Modals/Apply";
import { InviteFriends } from "@pro/containers/Modals/InviteFriends";
import { ReferCandidate } from "@pro/containers/Modals/Refer";
import { ReferThankYou } from "@pro/containers/Modals/ReferThankYou";
import { SendMessage } from "@pro/containers/Modals/SendMessage";
import { ShareJob } from "@pro/containers/Modals/ShareJob";
import { PageInternal } from "@pro/containers/PageInternal";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

import { LeaveJob } from "../Modals/LeaveJob";

/*===========================================================================
  Jobs Component
/*======================================================================== */
interface JobsProps extends RouteComponentProps<any> {}
interface ApplyResultModel {
    job_candidate_interest_token: string;
    job_id: number;
    newJobCandidateId: number;
}
interface ContextJobModel {
    job_id: number;
    job_name: string;
    job_type: JobType;
    user_can_leave: boolean;
    total_commission_in_us_dollars: string;
    location: string;
}
interface JobsState {
    advisor: {
        status: string;
        response: {
            user_id: number;
            profile_image_url: string;
            last_name: string;
            first_name: string;
            role_name: string;
            phone_number_for_url: string;
            phone_number: string;
        };
    };
    error: boolean;
    errorMessage: string;
    updating: boolean;
    loading: boolean;
    contextJob: ContextJobModel;
    isLargeScreen: boolean;
    inviteFriendsModalIsOpen: boolean;
    filtersModalIsOpen: boolean;
    referCandidateModalIsOpen: boolean;
    referThankYouModalIsOpen: boolean;
    referResult: {
        jobCandidateId?: number;
    };
    applyForJobModalIsOpen: boolean;
    applyThankYouModalIsOpen: boolean;
    applyResult: ApplyResultModel;
    shareJobModalIsOpen: boolean;
    leaveJobModalIsOpen: boolean;
    bestTimesModalIsOpen: boolean;
    messageCoachModalIsOpen: boolean;
    activeJobId?: number;
    search: SearchOptions;
    locationSearchTerm: string;
    results: ResultsModel;
    selected_location: FormField;
}
interface ResultsModel {
    results: any[];
    selected_facets: FacetModel[];
    facets: FacetModel[];
    search_radius_in_miles: number;
}

export class Jobs extends React.Component<JobsProps, JobsState> {
    private _abortControllers: { [key: string]: AbortController } = {};
    searchService: SearchService;
    removeHistoryListener: () => void;
    defaultOrderBy: number;

    constructor(props) {
        super(props);

        this.onPageChange = this.onPageChange.bind(this);
        this.onDistanceChange = this.onDistanceChange.bind(this);
        this.onMobileDistanceChange = this.onMobileDistanceChange.bind(this);
        this.onFacetChange = this.onFacetChange.bind(this);
        this.onSortChange = this.onSortChange.bind(this);
        this.onKeywordSubmit = this.onKeywordSubmit.bind(this);
        this.onMobileKeywordSubmit = this.onMobileKeywordSubmit.bind(this);
        this.onMobileKeywordChanged = this.onMobileKeywordChanged.bind(this);
        this.onMobileLocationSubmit = this.onMobileLocationSubmit.bind(this);
        this.onFiltersButtonClick = this.onFiltersButtonClick.bind(this);
        this.onUrlChange = this.onUrlChange.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);

        this.getLocationSearchTerm = this.getLocationSearchTerm.bind(this);
        this.getScopeFacet = this.getScopeFacet.bind(this);
        this.getTeamFacet = this.getTeamFacet.bind(this);

        this.emptySearchOptions = this.emptySearchOptions.bind(this);
        this.fetchJobs = this.fetchJobs.bind(this);

        this.onLeaveJobOkay = this.onLeaveJobOkay.bind(this);
        this.signAgreement = this.signAgreement.bind(this);
        this.setUserHasAccess = this.setUserHasAccess.bind(this);

        this.goToReferral = this.goToReferral.bind(this);
        this.onApplyOkay = this.onApplyOkay.bind(this);
        this.onReferOkay = this.onReferOkay.bind(this);
        this.onReferThanksGoToReferral = this.onReferThanksGoToReferral.bind(
            this
        );
        this.getLocations = this.getLocations.bind(this);
        this.searchService = new SearchService(
            this.emptySearchOptions,
            this.fetchJobs,
            this.props.history
        );

        this.state = {
            advisor: {
                response: {
                    user_id: 0,
                    first_name: "",
                    last_name: "",
                    phone_number: "",
                    phone_number_for_url: "",
                    profile_image_url: "",
                    role_name: "",
                },
                status: "pending",
            },
            error: false,
            errorMessage: "",
            updating: true,
            loading: false,
            contextJob: {
                job_id: 0,
                job_name: "",
                job_type: JobType.FULLTIME,
                user_can_leave: false,
                total_commission_in_us_dollars: "",
                location: "",
            },
            isLargeScreen: false,
            inviteFriendsModalIsOpen: false,
            filtersModalIsOpen: false,
            referCandidateModalIsOpen: false,
            referThankYouModalIsOpen: false,
            referResult: { jobCandidateId: undefined },
            applyForJobModalIsOpen: false,
            applyThankYouModalIsOpen: false,
            applyResult: {
                job_candidate_interest_token: "",
                job_id: 0,
                newJobCandidateId: 0,
            },
            shareJobModalIsOpen: false,
            leaveJobModalIsOpen: false,
            bestTimesModalIsOpen: false,
            messageCoachModalIsOpen: false,
            activeJobId: undefined,
            search: this.emptySearchOptions(),
            locationSearchTerm: "",
            results: {
                results: [] as any[],
                facets: [] as FacetModel[],
                search_radius_in_miles: 50,
                selected_facets: [] as FacetModel[],
            },
            selected_location: {
                name: "selected_location_id",
                label: "",
                value: 0,
                isValid: true,
                validationMessages: [],
                rules: [],
            },
        };
    }

    componentDidMount() {
        if (this.state.advisor.status !== "success") {
            // Check to see if there already was an abortController, and if so, call abort on it
            if (this._abortControllers.getManager) {
                this._abortControllers.getManager.abort();
            }
            this._abortControllers.getManager = new AbortController();
            const signal = this._abortControllers.getManager.signal;
            SettingsService.getManager(signal).then(resp => {
                this.setState({
                    advisor: { status: "success", response: resp },
                });
            });
        }
        this.searchService.updateFromUrl(this.props.location);
        this.removeHistoryListener = this.props.history.listen(
            this.onUrlChange
        );
    }

    componentWillMount() {
        this.setState({
            isLargeScreen: window.matchMedia(
                "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
            ).matches,
        });
        // TODO: remove this event hanlder when we leave this component
        window.onresize = () => {
            this.setState({
                isLargeScreen: window.matchMedia(
                    "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
                ).matches,
            });
        };
    }

    componentWillUnmount() {
        if (this.removeHistoryListener) this.removeHistoryListener();
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key =>
                abortControllers[key].abort()
            );
        }
    }

    emptySearchOptions(): SearchOptions {
        return {
            scope: 0,
            search_term: "",
            search_radius_in_miles: 50,
            current_page: 1,
            page_size: 25,
            order_by: null, // let BE/SQL determine default sort
            facets: [],
            team_id: null,
        };
    }

    getLocations(term: string) {
        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.suggestLocations) {
            this._abortControllers.suggestLocations.abort();
        }
        this._abortControllers.suggestLocations = new AbortController();
        const signal = this._abortControllers.suggestLocations.signal;

        return LocationService.suggestLocations(term, false, true, signal).then(
            result => {
                return result.results.map(x => ({
                    label: x.item_name,
                    value: x.item_id,
                }));
            }
        );
    }

    onUrlChange(location: any) {
        this.searchService.updateFromUrl(location);
    }

    onKeywordLocationChange = (value: {
        keyword: string;
        location: { value };
    }) => {
        const { keyword, location } = value;

        this.closeModal("filtersModalIsOpen");
        this.searchService.updateUrl({
            ...this.emptySearchOptions(),
            search_term: keyword,
            location: location ? location.value : this.state.search.location,
            order_by: 14,
        });
    };

    onPageChange(pageNumber: number) {
        this.searchService.updateUrl({
            ...this.state.search,
            current_page: pageNumber,
        });
    }

    onFacetChange(facet: FacetModel) {
        this.closeModal("filtersModalIsOpen");

        const defaultOptions = this.emptySearchOptions();
        const options = {
            ...this.state.search,
            current_page: defaultOptions.current_page,
        };
        const facets = [] as FacetModel[];
        if (options.facets) {
            options.facets.forEach(item => {
                if (item.type !== facet.type) {
                    facets.push(item);
                }
            });
        }
        facets.push(facet);

        facets.forEach(item => {
            if (item.key === "location") {
                const opt = item.options.find(el => el.selected === true);
                if (opt) {
                    // location_id = opt.value;
                    options.location = opt.value;
                } else {
                    // If the user has "undone" a location facet, we reset the search radius to the default value
                    options.search_radius_in_miles = this.emptySearchOptions().search_radius_in_miles;
                    options.location = undefined;
                }
            }

            // Scope is a "fake" facet. we make it a facet on the client,
            //  but the server handles it as a separate search parameter
            if (item.key === "scope") {
                options.scope =
                    item.options &&
                    item.options.length &&
                    item.options[0].selected
                        ? item.options[0].value
                        : 0;
            }

            if (item.key === "team_id") {
                options.team_id = item.options[0].selected
                    ? item.options[0].value
                    : undefined;
            }
        });

        options.facets = facets;

        this.searchService.updateUrl(options);
    }

    onSortChange(sort: number) {
        if (!sort) return;

        this.searchService.updateUrl({
            ...this.state.search,
            order_by: sort,
            current_page: 1,
        });
    }

    onDistanceChange(distance: number) {
        this.closeModal("filtersModalIsOpen");
        this.searchService.updateUrl({
            ...this.state.search,
            search_radius_in_miles: distance,
        });
    }

    onMobileDistanceChange(distance: number) {
        this.closeModal("filtersModalIsOpen");
        this.searchService.updateUrl({
            ...this.state.search,
            search_radius_in_miles: distance,
        });
    }

    onMobileKeywordChanged(newValue: string) {
        this.setState(prevState => {
            return {
                ...prevState,
                search: {
                    ...prevState.search,
                    search_term: newValue,
                    order_by: 14,
                },
            };
        });
    }

    onKeywordSubmit(keyword) {
        this.closeModal("filtersModalIsOpen");
        // A keyword search resets everything except location
        this.searchService.updateUrl({
            ...this.emptySearchOptions(),
            search_term: keyword,
            location: this.state.search.location,
            order_by: 14,
        });
    }

    onMobileKeywordSubmit(keyword) {
        this.closeModal("filtersModalIsOpen");
        // A keyword search resets everything except location
        this.searchService.updateUrl({
            ...this.emptySearchOptions(),
            search_term: keyword,
            location: this.state.search.location,
            order_by: 14,
        });
    }

    onMobileLocationSubmit(
        name: string,
        selected_item: { label: string; value: any }
    ) {
        this.closeModal("filtersModalIsOpen");

        const field = this.state.selected_location;
        FormHandler.handleInputChange(name, selected_item, [field]);
        this.setState({ selected_location: field }, () => {
            // A keyword search resets everything except keyword
            this.searchService.updateUrl({
                ...this.emptySearchOptions(),
                location: selected_item ? selected_item.value : null,
                search_term: this.state.search.search_term,
            });
        });
    }

    onFiltersButtonClick() {
        return; // TODO
    }

    openModal(modal: string, contextJob = this.state.contextJob) {
        const state = { contextJob };
        state[modal] = true;
        this.setState(state);
    }
    closeModal(modal: string) {
        const state = {};
        state[modal] = false;
        this.setState(state);
    }

    onLeaveJobOkay(job: ContextJobModel) {
        this.setState(
            { contextJob: job, leaveJobModalIsOpen: false, updating: true },
            () => {
                this.setUserHasAccess(job.job_id, false)
                    .then(() => this.setState({ updating: false }))
                    .catch(error => this.setState({ error, updating: false }));
            }
        );
    }

    signAgreement(job: ContextJobModel) {
        this.setState({ contextJob: job, updating: true }, () => {
            JobService.signAgreement(job.job_id)
                .then(() => this.setUserHasAccess(job.job_id, true))
                .then(() => this.setState({ updating: false }))
                .catch(error => this.setState({ error, updating: false }));
        });
    }

    setUserHasAccess(jobId: number, userHasAccess: boolean) {
        return new Promise((resolve, reject) => {
            const jobs = this.state.results.results;
            const updatedJob = jobs.find(j => j.job_id === jobId);

            if (!updatedJob) {
                reject("Could not find job in jobs list.");
            }

            updatedJob.user_has_access = userHasAccess;
            this.setState(
                prevState => {
                    return {
                        ...prevState,
                        results: {
                            ...prevState.results,
                            results: [...jobs],
                        },
                    };
                },
                () => resolve(updatedJob)
            );
        });
    }

    goToApplication = (jobCandidateId: number) => {
        this.props.history.push("/applications/" + jobCandidateId);
    };

    goToReferral(jobCandidateId: number) {
        this.props.history.push("/referrals/" + jobCandidateId);
    }

    onApplyOkay(results: ApplyResultModel) {
        this.setState({ applyResult: results }, () => {
            this.closeModal("applyForJobModalIsOpen");
            this.openModal("applyThankYouModalIsOpen");
        });
    }

    onReferOkay(jobCandidateId: number) {
        this.setState({ referResult: { jobCandidateId } }, () => {
            this.closeModal("referCandidateModalIsOpen");
            this.openModal("referThankYouModalIsOpen");
        });
    }

    onApplyThanksGoToApplication = (jobCandidateId?: number) => {
        this.closeModal("applyThankYouModalIsOpen");

        this.goToApplication(
            jobCandidateId || this.state.applyResult.newJobCandidateId
        );
    };

    onReferThanksGoToReferral() {
        this.closeModal("referThankYouModalIsOpen");
        this.goToReferral(this.state.referResult.jobCandidateId || 0);
    }

    getLocationSearchTerm(searchResult: ResultsModel) {
        const locationFacets = searchResult.selected_facets.filter(
            f => f.type === Search.FacetType.JOB_LOCATION.value
        );
        return locationFacets.length
            ? {
                  label: locationFacets[0].options[0].label,
                  value: locationFacets[0].options[0].value,
              }
            : null;
    }

    getTeamFacet(searchResult) {
        if (searchResult.team_id) {
            return {
                key: "team_id",
                title: "team",
                type: -999,
                options: [
                    {
                        count: 1,
                        label: "Shared Jobs",
                        selected: true,
                        value: searchResult.team_id,
                    },
                ],
            };
        }
        return undefined;
    }

    getScopeFacet(searchResult) {
        // Scope is not really a facet. We just pretend that it is
        //  in order to get the 'undo' behavior
        const scopeFacetKeys = ["all", "recommended", "my"];
        const scopeFacets = {
            all: {
                key: "scope",
                title: "scope",
                type: -999,
                options: [
                    {
                        count: 1,
                        label: "Recommended Jobs",
                        selected: true,
                        value: 0,
                    },
                ],
            },
            recommended: {
                key: "scope",
                title: "scope",
                type: -999,
                options: [
                    {
                        count: 1,
                        label: "Recommended Jobs",
                        selected: true,
                        value: 1,
                    },
                ],
            },
            my: {
                key: "scope",
                title: "scope",
                type: -999,
                options: [
                    {
                        count: 1,
                        label: "My Jobs",
                        selected: true,
                        value: 2,
                    },
                ],
            },
        };

        const defaultSearch = this.emptySearchOptions();
        return searchResult.scope && searchResult.scope !== defaultSearch.scope
            ? scopeFacets[scopeFacetKeys[searchResult.scope]]
            : undefined;
    }

    fetchJobs(options) {
        this.setState({ updating: true }, () => {
            // Check to see if there already was an abortController, and if so, call abort on it
            if (this._abortControllers.search) {
                this._abortControllers.search.abort();
            }
            this._abortControllers.search = new AbortController();
            const searchSignal = this._abortControllers.search.signal;

            JobService.search(options, searchSignal)
                .then(result => {
                    const defaultSearch = this.emptySearchOptions();
                    const search = {
                        scope: result.scope || defaultSearch.scope,
                        team_id: result.team_id || undefined,
                        search_term:
                            result.search_term || defaultSearch.search_term,
                        current_page:
                            result.current_page || defaultSearch.current_page,
                        page_size: result.page_size || defaultSearch.page_size,
                        order_by: result.order_by || defaultSearch.order_by,
                        search_radius_in_miles:
                            result.search_radius_in_miles ||
                            defaultSearch.search_radius_in_miles,
                        facets: result.selected_facets || defaultSearch.facets,
                    };

                    const locationSearchTerm = this.getLocationSearchTerm(
                        result
                    );
                    const scopeFacet = this.getScopeFacet(result);
                    const teamFacet = this.getTeamFacet(result);

                    if (scopeFacet) {
                        result.selected_facets.push(scopeFacet);
                    }
                    if (teamFacet) {
                        result.selected_facets.push(teamFacet);
                    }

                    this.setState(
                        {
                            locationSearchTerm: locationSearchTerm
                                ? locationSearchTerm.label
                                : "",
                            selected_location: {
                                ...this.state.selected_location,
                                value: locationSearchTerm,
                            },
                            error: false,
                            loading: false,
                            updating: false,
                            search: {
                                ...search,
                                location: locationSearchTerm
                                    ? locationSearchTerm.value
                                    : null,
                            },
                            results: result,
                        },
                        () => window.scrollTo(0, 0)
                    );
                })
                .catch(err => {
                    console.log(err);
                    if (err.type === "abort" || err.name === "AbortError") {
                        return;
                    }

                    this.setState(
                        {
                            error: true,
                            errorMessage: err.message,
                            updating: false,
                        },
                        () => window.scrollTo(0, 0)
                    );
                });
        });
    }

    render() {
        const {
            search,
            locationSearchTerm,
            error,
            updating,
            errorMessage,
            isLargeScreen,
            bestTimesModalIsOpen,
        } = this.state;
        const [jobs, selectedFacets, facets, search_radius_in_miles] = this
            .state.results
            ? [
                  this.state.results.results,
                  this.state.results.selected_facets,
                  this.state.results.facets,
                  this.state.results.search_radius_in_miles,
              ]
            : [[], [], [], 50];

        if (error) {
            return (
                <PageInternal title="jobs">
                    <Error show={!!error} description={errorMessage} />
                </PageInternal>
            );
        } else if (updating && !jobs.length) {
            return (
                <PageInternal title="jobs">
                    <Loading />
                </PageInternal>
            );
        } else if (!jobs.length) {
            return (
                <PageInternal title="jobs">
                    <h2
                        className="c-heading c-heading--purple"
                        data-cy="pro-search-no-results-header"
                    >
                        we didn't find any jobs matching your search, but we are
                        adding new positions every day!
                    </h2>
                    <div className="u-text-center">
                        <div className="c-block c-block--space-evenly c-block--narrow">
                            <Link
                                className="c-button c-button--green"
                                to={Routing.Client.Pro.Jobs.SEARCH}
                            >
                                expand your search
                            </Link>
                            <Button
                                id="ask-coach-button"
                                onClick={() =>
                                    this.openModal("messageCoachModalIsOpen")
                                }
                            >
                                ask coach for help
                            </Button>
                            <SendMessage
                                isOpen={this.state.messageCoachModalIsOpen}
                                closeModal={() =>
                                    this.closeModal("messageCoachModalIsOpen")
                                }
                                onCancel={() =>
                                    this.closeModal("messageCoachModalIsOpen")
                                }
                                toUserIds={[
                                    this.state.advisor.status === "success" &&
                                    this.state.advisor.response.user_id
                                        ? this.state.advisor.response.user_id
                                        : -1,
                                ]}
                            />
                        </div>
                    </div>
                </PageInternal>
            );
        }

        return (
            <PageInternal
                updating={updating}
                title="jobs"
                center={
                    <JobsPageHeaderCenter
                        keyword={search.search_term}
                        locationKeyword={locationSearchTerm}
                        onChange={this.onKeywordLocationChange}
                    />
                }
            >
                <FilteredList>
                    <FilteredListTableView
                        responsive={true}
                        data={this.state.results}
                        searchRadiusInMiles={
                            this.state.results.search_radius_in_miles
                        }
                        locationSearchTerm={locationSearchTerm}
                        onPageChange={this.onPageChange}
                        onFacetChange={this.onFacetChange}
                        onDistanceChange={this.onDistanceChange}
                        isLargeScreen={isLargeScreen}
                    >
                        <table className="c-table c-table--jobs">
                            <thead>
                                <tr>
                                    <th>
                                        <Sort
                                            sortKey={SortKeys.Job.JOB}
                                            orderBy={
                                                search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(
                                                    SortKeys.Job.JOB
                                                )
                                            }
                                        >
                                            Job
                                        </Sort>
                                        <Sort
                                            sortKey={SortKeys.Job.CATEGORY}
                                            orderBy={
                                                search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(
                                                    SortKeys.Job.CATEGORY
                                                )
                                            }
                                        >
                                            Category
                                        </Sort>
                                    </th>
                                    <th>
                                        <Sort
                                            sortKey={SortKeys.Job.LOCATION}
                                            orderBy={
                                                search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(
                                                    SortKeys.Job.LOCATION
                                                )
                                            }
                                        >
                                            Location
                                        </Sort>
                                    </th>
                                    <th>
                                        <Sort
                                            sortKey={SortKeys.Job.TYPE}
                                            orderBy={
                                                search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(
                                                    SortKeys.Job.TYPE
                                                )
                                            }
                                        >
                                            Type
                                        </Sort>
                                    </th>
                                    <th>
                                        <Sort
                                            sortKey={
                                                SortKeys.Job
                                                    .QUANTITY_MINUS_HIRED_JOB_CANDIDATE_COUNT
                                            }
                                            orderBy={
                                                search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(
                                                    SortKeys.Job
                                                        .QUANTITY_MINUS_HIRED_JOB_CANDIDATE_COUNT
                                                )
                                            }
                                        >
                                            Openings
                                        </Sort>
                                    </th>
                                    <th>
                                        <Sort
                                            sortKey={
                                                SortKeys.Job.REFERRAL_REWARD
                                            }
                                            orderBy={
                                                search.order_by ||
                                                this.defaultOrderBy
                                            }
                                            onSortChange={() =>
                                                this.onSortChange(
                                                    SortKeys.Job.REFERRAL_REWARD
                                                )
                                            }
                                        >
                                            Referral Reward
                                        </Sort>
                                    </th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.map(job => (
                                    <tr key={job.job_id}>
                                        <td>
                                            <div className="c-table__job-name">
                                                <Link
                                                    to={formatClientRoute(
                                                        Routing.Client.Jobs
                                                            .DETAILS,
                                                        {
                                                            id: job.job_id,
                                                        }
                                                    )}
                                                    className="c-link no-underline"
                                                >
                                                    {job.job_name}
                                                </Link>
                                            </div>
                                            <div className="c-table__job-category">
                                                {
                                                    job.primary_job_position_display_name
                                                }
                                            </div>
                                        </td>
                                        <td>{job.location}</td>
                                        <td>
                                            <span className="u-text-nowrap">
                                                {job.job_type}
                                            </span>
                                        </td>
                                        <td>{job.open_positions}</td>
                                        <td>
                                            {job.total_commission_in_us_dollars !==
                                            ""
                                                ? job.total_commission_in_us_dollars
                                                : "N/A"}
                                        </td>
                                        <td>
                                            <Dropdown right={true}>
                                                <Button
                                                    id="dropdown-toggle-button"
                                                    dropdown={true}
                                                    className="c-dropdown__toggle"
                                                    onClick={() => {
                                                        return;
                                                    }}
                                                >
                                                    options
                                                </Button>
                                                <DropdownMenu>
                                                    <DropdownItem
                                                        linkTo={formatClientRoute(
                                                            Routing.Client.Pro
                                                                .Jobs.DETAILS,
                                                            { id: job.job_id }
                                                        )}
                                                    >
                                                        view job
                                                    </DropdownItem>

                                                    <DropdownItem
                                                        onClick={() =>
                                                            this.openModal(
                                                                "referCandidateModalIsOpen",
                                                                job
                                                            )
                                                        }
                                                    >
                                                        refer a candidate
                                                    </DropdownItem>
                                                    <DropdownItem
                                                        onClick={() =>
                                                            this.openModal(
                                                                "applyForJobModalIsOpen",
                                                                job
                                                            )
                                                        }
                                                    >
                                                        apply for position
                                                    </DropdownItem>

                                                    <DropdownItem
                                                        onClick={() =>
                                                            this.openModal(
                                                                "shareJobModalIsOpen",
                                                                job
                                                            )
                                                        }
                                                    >
                                                        share job
                                                    </DropdownItem>
                                                    {!job.user_has_access && (
                                                        <DropdownItem
                                                            onClick={() =>
                                                                this.signAgreement(
                                                                    job
                                                                )
                                                            }
                                                        >
                                                            add to my jobs
                                                        </DropdownItem>
                                                    )}
                                                    {job.user_has_access && (
                                                        <DropdownItem
                                                            onClick={() =>
                                                                this.openModal(
                                                                    "leaveJobModalIsOpen",
                                                                    job
                                                                )
                                                            }
                                                        >
                                                            remove from my jobs
                                                        </DropdownItem>
                                                    )}
                                                </DropdownMenu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </FilteredListTableView>

                    <FilteredListCardsView
                        responsive={true}
                        data={this.state.results}
                        onPageChange={this.onPageChange}
                        onFiltersButtonClick={() =>
                            this.openModal("filtersModalIsOpen")
                        }
                        sortDropdown={
                            <JobsSortDropdown
                                onSortChange={this.onSortChange}
                            />
                        }
                        isLargeScreen={isLargeScreen}
                    >
                        {jobs.map(job => (
                            <Card
                                id={job.job_id}
                                ribbon={null}
                                linkTo={formatClientRoute(
                                    Routing.Client.Jobs.DETAILS,
                                    {
                                        id: job.job_id,
                                    }
                                )}
                                title={job.job_name}
                                key={job.job_id}
                            >
                                <CardItem bullet={true}>
                                    {job.primary_job_position_display_name}
                                </CardItem>
                                <CardItem bullet={true}>
                                    {job.location}
                                </CardItem>
                                <CardItem bullet={true}>
                                    {job.job_type}
                                </CardItem>
                                <CardItem>
                                    {job.open_positions} opening
                                    {Number(job.open_positions) !== 1
                                        ? "s"
                                        : ""}
                                </CardItem>
                                <CardItem>
                                    {job.total_commission_in_us_dollars}{" "}
                                    {job.total_commission_in_us_dollars !== ""
                                        ? " referral reward"
                                        : "referral reward not available"}
                                </CardItem>
                            </Card>
                        ))}
                    </FilteredListCardsView>
                </FilteredList>

                <InviteFriends
                    isOpen={this.state.inviteFriendsModalIsOpen}
                    closeModal={() =>
                        this.closeModal("inviteFriendsModalIsOpen")
                    }
                />

                <ReferCandidate
                    isOpen={this.state.referCandidateModalIsOpen}
                    job={this.state.contextJob}
                    onOkay={this.onReferOkay}
                    onCancel={() =>
                        this.closeModal("referCandidateModalIsOpen")
                    }
                    closeModal={() =>
                        this.closeModal("referCandidateModalIsOpen")
                    }
                />

                <ReferThankYou
                    isOpen={this.state.referThankYouModalIsOpen}
                    job={this.state.contextJob}
                    onViewJobs={() =>
                        this.closeModal("referThankYouModalIsOpen")
                    }
                    onGoToReferral={this.onReferThanksGoToReferral}
                    closeModal={() =>
                        this.closeModal("referThankYouModalIsOpen")
                    }
                />

                <ApplyForJob
                    isOpen={this.state.applyForJobModalIsOpen}
                    job={this.state.contextJob}
                    onOkay={this.onApplyOkay}
                    onCancel={() => this.closeModal("applyForJobModalIsOpen")}
                    closeModal={() => this.closeModal("applyForJobModalIsOpen")}
                    onViewJobs={() => this.closeModal("applyForJobModalIsOpen")}
                    onGoToApplication={this.onApplyThanksGoToApplication}
                />

                <ApplyThankYou
                    isOpen={this.state.applyThankYouModalIsOpen}
                    job={this.state.contextJob}
                    onViewJobs={() =>
                        this.closeModal("applyThankYouModalIsOpen")
                    }
                    closeModal={() =>
                        this.closeModal("applyThankYouModalIsOpen")
                    }
                    onGoToApplication={this.onApplyThanksGoToApplication}
                />

                <ShareJob
                    isOpen={this.state.shareJobModalIsOpen}
                    job={this.state.contextJob}
                    closeModal={() => this.closeModal("shareJobModalIsOpen")}
                />

                <LeaveJob
                    isOpen={this.state.leaveJobModalIsOpen}
                    jobId={this.state.contextJob.job_id}
                    jobName={this.state.contextJob.job_name}
                    onOkay={() => this.onLeaveJobOkay(this.state.contextJob)}
                    onCancel={() => this.closeModal("leaveJobModalIsOpen")}
                    closeModal={() => this.closeModal("leaveJobModalIsOpen")}
                />

                <Modal
                    className="c-modal--filters"
                    size="full"
                    isOpen={this.state.filtersModalIsOpen}
                    closeModal={() => this.closeModal("filtersModalIsOpen")}
                >
                    <div className="c-modal__filters">
                        <div className="c-modal__filters-title">filters</div>
                        {selectedFacets && selectedFacets.length > 0 && (
                            <SelectedFacets
                                overlay={true}
                                data={selectedFacets}
                                onFacetChange={this.onFacetChange}
                            />
                        )}
                        <SearchEntry
                            label="keywords"
                            overlay={true}
                            dark={true}
                            submitOnEnter={true}
                            keyword={search.search_term}
                            onTypedValueChanged={this.onMobileKeywordChanged}
                            onSubmit={this.onMobileKeywordSubmit}
                        />

                        <Select
                            name={this.state.selected_location.name}
                            label="location"
                            value={this.state.selected_location.value}
                            isValid={this.state.selected_location.isValid}
                            validationMessages={[]}
                            onChange={this.onMobileLocationSubmit}
                            multi={false}
                            remoteOptionsMethod={this.getLocations}
                        />

                        <Facets
                            overlay={true}
                            searchRadiusInMiles={search_radius_in_miles}
                            locationSearchTerm={locationSearchTerm}
                            facets={facets}
                            onFacetChange={this.onFacetChange}
                            onDistanceChange={this.onMobileDistanceChange}
                        />
                    </div>
                </Modal>
            </PageInternal>
        );
    }
}
